import {
  GetAvailableSearchCriteriaForMessagesReportRequest,
  IGetAvailableSearchCriteriaForMessagesReportRequest,
  IGetAvailableSearchCriteriaForMessagesReportResponse,
  IGetAvailableSearchCriteriaForPostQualReportResponse,
  IGetAvailableSearchCriteriaForPostQualReportRequest,
  GetAvailableSearchCriteriaForPostQualReportRequest,
  ISysPrinDto,
  KeyValuePairOfInclusionOperatorAndString,
  OlmMessageStatus,
  RequirementRestriction,
  SysPrin,
} from '@/api/client';
import Api from '@/api/helpers';
import { IComboItem } from '@/components/shared/ComboBox';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IErrorDetails, IRequestState } from './interfaces';

export interface IReportsState {
  reportsSearchCriteria: {
    availableSysPrins: ISysPrinDto[];
    availableDocumentTypes: IComboItem[] | undefined;
    rawAvailableDocumentTypes: { [key: string]: string } | undefined;
    availableLocations: IComboItem[] | undefined;
    rawAvailableLocations: { [key: string]: string } | undefined;
    availableMessageRequirementOptions: IComboItem[] | undefined;
    availableRequestorDepartments: IComboItem[] | undefined;
    rawAvailableRequestorDepartments: { [key: string]: string } | undefined;
    availableRequestorDepartmentsPostCycle: IComboItem[] | undefined;
    /** When true, Post Qual may use Statement Validation (B) Snowflake data (CSG Connect StatementValidation app). */
    showStatementValidationDataSourceOption: boolean;
    availableSelectivityInclusionOperators: IComboItem[] | undefined;
    availableMessageStatuses: IComboItem[] | undefined;
    availableLanguages: IComboItem[] | undefined;
    availableGraphicIds: IComboItem[] | undefined;
  } & IRequestState;
}

const initialState: IReportsState = {
  reportsSearchCriteria: {
    availableSysPrins: [],
    availableDocumentTypes: [],
    rawAvailableDocumentTypes: undefined,
    availableLocations: [],
    rawAvailableLocations: undefined,
    availableMessageRequirementOptions: [],
    availableRequestorDepartments: [],
    rawAvailableRequestorDepartments: undefined,
    availableRequestorDepartmentsPostCycle: [],
    showStatementValidationDataSourceOption: false,
    availableSelectivityInclusionOperators: [],
    availableMessageStatuses: [],
    availableLanguages: [],
    availableGraphicIds: [],
    isRequestActive: false,
    errorDetails: { statusCode: null, userMessage: null },
  },
};

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setDefaultValue: (state) => {
      state.reportsSearchCriteria.availableDocumentTypes = [];
      state.reportsSearchCriteria.rawAvailableDocumentTypes = undefined;
      state.reportsSearchCriteria.availableLocations = [];
      state.reportsSearchCriteria.rawAvailableLocations = undefined;
      state.reportsSearchCriteria.availableMessageRequirementOptions = [];
      state.reportsSearchCriteria.availableRequestorDepartments = [];
      state.reportsSearchCriteria.rawAvailableRequestorDepartments = undefined;
      state.reportsSearchCriteria.availableRequestorDepartmentsPostCycle = [];
      state.reportsSearchCriteria.showStatementValidationDataSourceOption = false;
      state.reportsSearchCriteria.availableSelectivityInclusionOperators = [];
      state.reportsSearchCriteria.availableMessageStatuses = [];
      state.reportsSearchCriteria.availableLanguages = [];
      state.reportsSearchCriteria.availableGraphicIds = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAvailableSearchCriteriaForMessagesReport.pending, (state) => {
      state.reportsSearchCriteria.isRequestActive = true;
      state.reportsSearchCriteria.errorDetails.statusCode = null;
    });
    builder.addCase(fetchAvailableSearchCriteriaForMessagesReport.rejected, (state) => {
      state.reportsSearchCriteria.isRequestActive = false;
      state.reportsSearchCriteria.errorDetails.statusCode = 500;
    });
    builder.addCase(fetchAvailableSearchCriteriaForPostQualReport.pending, (state) => {
      state.reportsSearchCriteria.isRequestActive = true;
    });
    builder.addCase(fetchAvailableSearchCriteriaForPostQualReport.fulfilled, (state, action) => {
      state.reportsSearchCriteria.isRequestActive = false;
      state.reportsSearchCriteria.availableSysPrins = getAvailableSysPrins(action.payload.availableSysPrins);
      state.reportsSearchCriteria.availableRequestorDepartmentsPostCycle = getComboItemNames(
        action.payload.availableRequestorDepartments,
      )?.sort(sortByName);
      state.reportsSearchCriteria.showStatementValidationDataSourceOption =
        action.payload.showStatementValidationDataSourceOption ?? false;
    });
    builder.addCase(fetchAvailableSearchCriteriaForMessagesReport.fulfilled, (state, action) => {
      state.reportsSearchCriteria.availableSysPrins = getAvailableSysPrins(action.payload.availableSysPrins);
      state.reportsSearchCriteria.rawAvailableDocumentTypes = action.payload.availableDocumentTypes;
      state.reportsSearchCriteria.availableDocumentTypes = getComboItemNames(
        action.payload.availableDocumentTypes,
      )?.sort(sortByName);
      state.reportsSearchCriteria.rawAvailableLocations = action.payload.availableLocations;
      state.reportsSearchCriteria.availableLocations = getComboItemNames(action.payload.availableLocations)?.sort(
        sortByName,
      );
      state.reportsSearchCriteria.availableMessageRequirementOptions = getComboItemNamesFromEnum(
        action.payload.availableMessageRequirementOptions,
        'requirement',
      );
      state.reportsSearchCriteria.rawAvailableRequestorDepartments = action.payload.availableRequestorDepartments;
      state.reportsSearchCriteria.availableRequestorDepartments = getComboItemNames(
        action.payload.availableRequestorDepartments,
      )?.sort(sortByName);
      state.reportsSearchCriteria.availableSelectivityInclusionOperators = getSelectivityInclusionOperators(
        action.payload.availableSelectivityInclusionOperators,
      );
      state.reportsSearchCriteria.availableMessageStatuses = getComboItemNamesFromEnum(
        action.payload.availableMessageStatuses,
        'status',
      );
      state.reportsSearchCriteria.availableLanguages = getComboItemsFromArrayString(action.payload.availableLanguages);
      state.reportsSearchCriteria.availableGraphicIds = getComboItemsFromArrayString(
        action.payload.availableGraphicIds,
      );
      state.reportsSearchCriteria.isRequestActive = false;
      state.reportsSearchCriteria.errorDetails.statusCode = null;
    });
  },
});

export const fetchAvailableSearchCriteriaForMessagesReport = createAsyncThunk<
  IGetAvailableSearchCriteriaForMessagesReportResponse,
  IGetAvailableSearchCriteriaForMessagesReportRequest,
  { rejectValue: IErrorDetails }
>('/reporting/getAvailableSearchCriteriaForMessagesReport', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getAvailableSearchCriteriaForMessagesReport(
    new GetAvailableSearchCriteriaForMessagesReportRequest(request),
  );
  return response;
});

export const fetchAvailableSearchCriteriaForPostQualReport = createAsyncThunk<
  IGetAvailableSearchCriteriaForPostQualReportResponse,
  IGetAvailableSearchCriteriaForPostQualReportRequest
>('/reporting/getAvailableSearchCriteriaForPostQualReport', async (request, thunkApi) => {
  const client = Api.getReportingClient(thunkApi.dispatch);
  const response = await client.getAvailableSearchCriteriaForPostQualReport(
    new GetAvailableSearchCriteriaForPostQualReportRequest(request),
  );
  return response;
});

const getAvailableSysPrins = (sysPrinsToProcess: SysPrin[] | undefined) => {
  let sysPrins: ISysPrinDto[] = [];
  if (sysPrinsToProcess) {
    sysPrins = sysPrinsToProcess.map((sp) => {
      return { ...sp, sysPrin: sp.system + '-' + sp.principle } as ISysPrinDto;
    });
  }
  return sysPrins;
};

const getComboItemNames = (itemsToProcess: { [key: string]: string } | undefined) => {
  if (itemsToProcess) {
    const keys = Object.keys(itemsToProcess);
    const items: IComboItem[] = [{ name: '' }];
    for (let i = 0; i < keys.length; i++) {
      const targetItemName = itemsToProcess[keys[i]];
      const alreadyInItems = items.find((item) => item.name === targetItemName);
      if (!alreadyInItems) {
        items.push({ name: targetItemName });
      }
    }
    return items;
  }
};

const getComboItemNamesFromEnum = (
  itemsToProcess: RequirementRestriction[] | OlmMessageStatus[] | undefined,
  type: 'requirement' | 'status',
) => {
  const items: IComboItem[] = [{ name: '' }];
  if (itemsToProcess) {
    itemsToProcess.forEach((item) => {
      if (type === 'requirement') items.push({ name: RequirementRestriction[item] });
      if (type === 'status') items.push({ name: OlmMessageStatus[item] });
    });
  }
  return items;
};

const getSelectivityInclusionOperators = (itemsToProcess: KeyValuePairOfInclusionOperatorAndString[] | undefined) => {
  const items: IComboItem[] = [{ name: '' }];
  if (itemsToProcess) {
    itemsToProcess.forEach((item) => {
      items.push({ name: item.value });
    });
  }
  return items;
};

const getComboItemsFromArrayString = (itemsToProcess: string[] | undefined) => {
  const items: IComboItem[] = [];
  if (itemsToProcess) {
    items.push({ name: '' });
    for (let i = 0; i < itemsToProcess.length; i++) {
      const alreadyInItems = items.find((item) => item.name === itemsToProcess[i]);
      if (!alreadyInItems) {
        items.push({ name: itemsToProcess[i] });
      }
    }
  }
  return items;
};

const sortByName = (a: IComboItem, b: IComboItem) => {
  if (a.name && b.name) {
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
  }
  return 0;
};

export const { setDefaultValue } = reportsSlice.actions;
export default reportsSlice.reducer;
